import { useEffect, useState } from "react";
import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, LifeBuoy, ArrowRight, ShieldCheck, AlertTriangle, Mail } from "lucide-react";
import useNoIndex from "@/hooks/useNoIndex";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import BookCover from "@/components/books/BookCover";
import Reveal from "@/components/books/Reveal";
import { HeroFade } from "@/components/books/KineticTitle";
import { CHINNU_BOOKS, CHINNU_BUNDLE, findChinnuProduct, QUICKSTART_TIPS, BOOKS_SUPPORT } from "@/data/books";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hidden post-purchase download page — /books/chinnu/order/<slug>
 * (slug = a book slug or "complete-collection").
 *
 * SECURITY GATE: a paid PDF is NEVER served without a verified payment.
 * Two verification paths, tried in order:
 *  A. `?token=...` — issued by /api/store/checkout/verify right after a
 *     Razorpay Standard Checkout success (the primary Buy Now flow). Just
 *     re-confirmed via GET /api/books/verify-token before unlocking the UI.
 *  B. Legacy Razorpay Payment Link callback params (`razorpay_payment_id`
 *     etc.) — kept working for the owner's 5 manual backup Payment Links,
 *     verified via POST /api/books/verify-return.
 * Anything else → "locked": a safe message + LifeIQ support contact, zero
 * download buttons rendered. Individual pages expose ONLY their own book;
 * the bundle page exposes all four books plus the ZIP — but only once verified.
 */
export default function OrderDownload() {
  useNoIndex();
  useTrackPageView("payment_success", { channel: "razorpay" });

  const { slug } = useParams();
  const [params] = useSearchParams();
  const product = findChinnuProduct(slug);
  const isBundle = slug === CHINNU_BUNDLE.slug;

  // checking → direct | verified | locked
  const [verify, setVerify] = useState({ state: "checking", token: null });

  useSEO({
    title: "Thank You — LifeIQ Books",
    description: "Your Chinnu adventure is ready. Download your digital edition.",
    canonicalPath: `/books/chinnu/order/${slug}`,
  });

  useEffect(() => {
    if (!product) return;
    const q = Object.fromEntries(params.entries());
    const directToken = params.get("token");
    const reference = q.razorpay_payment_id || params.get("ref") || null;
    track("payment_return", {
      product_id: product.product_id,
      offer_price: product.price_value,
      regular_price: product.regular_price_value,
      has_razorpay_params: Boolean(q.razorpay_payment_id || directToken),
      reference,
    });

    // Path A: arriving straight from a verified Standard Checkout (BuyButton
    // already got a signed token back from /api/store/checkout/verify) — just
    // re-confirm the token is genuinely valid before unlocking any UI.
    if (directToken) {
      fetch(`${API}/api/books/verify-token?slug=${encodeURIComponent(slug)}&token=${encodeURIComponent(directToken)}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.verified) setVerify({ state: "verified", token: directToken });
          else setVerify({ state: "locked", token: null, reason: "invalid_token" });
        })
        .catch(() => setVerify({ state: "locked", token: null, reason: "network_error" }));
      return;
    }

    // Path B: backward-compatible fallback for the owner's 5 manual Razorpay
    // Payment Links (kept live per owner request) if their callback params land here.
    fetch(`${API}/api/books/verify-return`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug,
        razorpay_payment_id: q.razorpay_payment_id || null,
        razorpay_payment_link_id: q.razorpay_payment_link_id || null,
        razorpay_payment_link_reference_id: q.razorpay_payment_link_reference_id || null,
        razorpay_payment_link_status: q.razorpay_payment_link_status || null,
        razorpay_signature: q.razorpay_signature || null,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.verified && data.token) setVerify({ state: "verified", token: data.token });
        else setVerify({ state: "locked", token: null, reason: data.verification || "unverified" });
      })
      .catch(() => setVerify({ state: "locked", token: null, reason: "network_error" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) return <Navigate to="/books/chinnu" replace />;

  const canDownload = verify.state === "verified";
  const checking = verify.state === "checking";
  const locked = verify.state === "locked";
  const reference = params.get("razorpay_payment_id") || params.get("ref") || "—";

  const dlUrl = (downloadSlug) => {
    const base = `/api/books/download/${downloadSlug}`;
    return verify.token ? `${base}?token=${encodeURIComponent(verify.token)}` : base;
  };

  const download = (downloadSlug, productId, isZip = false) => {
    if (!canDownload) return;
    track(isZip ? "bundle_zip_download" : "pdf_download_click", {
      product_id: productId,
      reference,
      verification: verify.state,
    });
    window.location.assign(dlUrl(downloadSlug));
  };

  const displayTitle = isBundle ? CHINNU_BUNDLE.title : product.title;
  const displayTelugu = isBundle ? CHINNU_BUNDLE.title_telugu : product.title_telugu;

  return (
    <main>
      <section className="bg-[#FBF7EE] min-h-[70vh] relative overflow-hidden">
        <span aria-hidden="true" className="font-telugu absolute -top-8 right-0 text-[16vw] leading-none text-[#F57F17]/[0.07] pointer-events-none select-none">
          ధన్యవాదాలు
        </span>
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-20 md:py-28 relative">
          <HeroFade delay={0.05}>
            <div className="text-center">
              <div
                className={`w-14 h-14 rounded-full text-white mx-auto flex items-center justify-center ${
                  locked ? "bg-[#B45309]" : "bg-[#004D40]"
                }`}
              >
                {locked ? <AlertTriangle size={26} /> : <CheckCircle2 size={28} />}
              </div>
              <div className="mt-6 text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">
                {checking ? "Confirming Your Payment" : locked ? "Verification Needed" : "Payment Successful"}
              </div>
              <h1 data-testid="order-download-title" className="mt-4 font-serif-d text-[32px] sm:text-5xl lg:text-[50px] leading-[1.08] text-[#0D1B2A]">
                {checking && "Confirming your payment…"}
                {canDownload && (
                  <>
                    Thank you!<br />{isBundle ? "Your complete Chinnu collection is ready." : "Your Chinnu adventure is ready."}
                  </>
                )}
                {locked && "We couldn't verify this purchase"}
              </h1>
              {canDownload && <p className="mt-4 font-telugu text-[19px] text-[#D86B0F]">ధన్యవాదాలు! మీ పుస్తకం సిద్ధంగా ఉంది</p>}
            </div>
          </HeroFade>

          {/* RECEIPT + DOWNLOAD (only rendered once payment is verified) */}
          {canDownload && (
            <Reveal delay={0.1}>
              <div data-testid="order-receipt-summary" className="mt-12 bg-white border border-[#E7DFCF] rounded-sm p-6 sm:p-8">
                {isBundle ? (
                  <div>
                    <div className="grid grid-cols-4 gap-3 max-w-[520px] mx-auto">
                      {CHINNU_BOOKS.map((b) => (
                        <BookCover key={b.slug} book={b} testId={`order-cover-${b.slug}`} />
                      ))}
                    </div>
                    <div className="mt-6 text-center">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C6470] font-semibold">Digital Collection · 4 PDFs</div>
                      <h2 className="mt-2 font-serif-d text-[24px] sm:text-[28px] text-[#0D1B2A] leading-[1.15]">{displayTitle}</h2>
                      <div className="mt-1 font-telugu text-[16px] text-[#D86B0F]">{displayTelugu}</div>
                      <div data-testid="order-language-attribute" className="mt-2 text-[12px] uppercase tracking-[0.14em] font-bold text-[#004D40]">
                        Digital Edition · PDF · English + <span className="font-telugu normal-case text-[13px]">తెలుగు</span>
                      </div>
                    </div>

                    <p className="mt-6 text-[14px] text-[#5C6470] text-center">Your digital books are ready. Tap any button below to download.</p>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {CHINNU_BOOKS.map((b) => (
                        <button
                          key={b.slug}
                          type="button"
                          data-testid={`download-book-${b.slug}`}
                          onClick={() => download(b.slug, b.product_id)}
                          className="inline-flex items-center justify-center gap-2 border border-[#0D1B2A] text-[#0D1B2A] px-5 py-3.5 text-[12px] font-semibold tracking-wide uppercase rounded-sm hover:bg-[#0D1B2A] hover:text-white transition-colors duration-200"
                        >
                          <Download size={14} /> Download Book {b.book_number} — {b.title}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      data-testid="download-pdf-button"
                      onClick={() => download(CHINNU_BUNDLE.slug, CHINNU_BUNDLE.product_id, true)}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#D86B0F] transition-colors duration-200 rounded-sm"
                    >
                      <Download size={15} /> Download All 4 Books — ZIP
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    <div className="md:col-span-4 max-w-[220px] mx-auto w-full">
                      <BookCover book={product} testId="order-book-cover" />
                    </div>
                    <div className="md:col-span-8">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C6470] font-semibold">
                        Book {product.book_number} · Digital Edition · PDF
                      </div>
                      <h2 className="mt-2 font-serif-d text-[24px] sm:text-[28px] text-[#0D1B2A] leading-[1.15]">{displayTitle}</h2>
                      {displayTelugu && <div className="mt-1 font-telugu text-[16px] text-[#D86B0F]">{displayTelugu}</div>}
                      <div data-testid="order-language-attribute" className="mt-2 text-[12px] uppercase tracking-[0.14em] font-bold text-[#004D40]">
                        Digital Edition · PDF · English + <span className="font-telugu normal-case text-[13px]">తెలుగు</span>
                      </div>

                      <p className="mt-5 text-[14px] text-[#5C6470] leading-[1.6]">
                        Your digital book is ready. Tap the button below to download your PDF.
                      </p>
                      <button
                        type="button"
                        data-testid="download-pdf-button"
                        onClick={() => download(product.slug, product.product_id)}
                        className="mt-4 inline-flex items-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#D86B0F] transition-colors duration-200 rounded-sm"
                      >
                        <Download size={15} /> Download Your Book
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-7 pt-5 border-t border-[#E5E7EA]">
                  <div data-testid="verification-badge" className="flex items-center gap-2 text-[13px] text-[#004D40] font-semibold">
                    <ShieldCheck size={15} /> Payment verified with Razorpay
                  </div>
                  <div className="mt-3 text-[13px] text-[#5C6470]">
                    <span className="uppercase tracking-[0.18em] font-semibold">Order reference</span>
                    <span data-testid="order-reference" className="ml-3 font-mono text-[14px] text-[#0D1B2A]">{reference}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* CHECKING STATE — no product content exposed while we wait */}
          {checking && (
            <Reveal delay={0.1}>
              <div data-testid="verification-checking" className="mt-12 bg-white border border-[#E7DFCF] rounded-sm p-8 sm:p-10 text-center">
                <div className="text-[14px] text-[#5C6470]">Confirming your payment with Razorpay…</div>
              </div>
            </Reveal>
          )}

          {/* LOCKED STATE — security gate: no PDF/ZIP access without a verified payment */}
          {locked && (
            <Reveal delay={0.1}>
              <div data-testid="verification-locked" className="mt-12 bg-white border border-[#B45309]/30 rounded-sm p-8 sm:p-10">
                <div className="flex items-start gap-4">
                  <AlertTriangle size={22} className="text-[#B45309] shrink-0 mt-0.5" />
                  <div>
                    <p data-testid="verification-locked-message" className="text-[16px] sm:text-[17px] text-[#0D1B2A] leading-[1.6]">
                      We couldn't verify this purchase. If you have already paid, please contact LifeIQ support with your payment details.
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px] text-[#0D1B2A]">
                      <a data-testid="locked-support-email" href={`mailto:${BOOKS_SUPPORT.email}`} className="inline-flex items-center gap-2 underline underline-offset-2 hover:text-[#D86B0F] transition-colors">
                        <Mail size={14} /> {BOOKS_SUPPORT.email}
                      </a>
                      <a data-testid="locked-support-whatsapp" href={BOOKS_SUPPORT.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 underline underline-offset-2 hover:text-[#D86B0F] transition-colors">
                        WhatsApp {BOOKS_SUPPORT.whatsapp}
                      </a>
                    </div>
                    <div className="mt-5 pt-4 border-t border-[#E5E7EA] text-[13px] text-[#5C6470]">
                      <span className="uppercase tracking-[0.18em] font-semibold">Order reference</span>
                      <span data-testid="order-reference" className="ml-3 font-mono text-[14px] text-[#0D1B2A]">{reference}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* QUICK-START GUIDE */}
          <Reveal delay={0.15}>
            <div data-testid="parent-quickstart-guide" className="mt-10 rounded-sm border border-[#E7DFCF] bg-white p-6 sm:p-8">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#004D40] font-semibold">Before the first page</div>
              <h3 className="mt-2 font-serif-d text-[22px] text-[#0D1B2A]">Four tips for tonight's first reading.</h3>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {QUICKSTART_TIPS.map((tip) => (
                  <div key={tip.n} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#F57F17] text-white font-serif-d text-[15px] flex items-center justify-center shrink-0">{tip.n}</div>
                    <div>
                      <div className="font-serif-d text-[16px] text-[#0D1B2A] leading-snug">{tip.t}</div>
                      <p className="mt-1 text-[13px] text-[#5C6470] leading-[1.6]">{tip.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* SUPPORT */}
          <Reveal delay={0.2}>
            <div className="mt-10 rounded-sm border border-[#E5E7EA] bg-white p-6 sm:p-7">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#004D40] text-white flex items-center justify-center shrink-0"><LifeBuoy size={18} /></div>
                <div>
                  <div className="font-serif-d text-[18px] text-[#0D1B2A]">Need help with your purchase or download?</div>
                  <p className="mt-1 text-[14px] text-[#5C6470] leading-[1.65]">
                    If the download doesn't start, refresh this page or write to us with your order reference. We reply within one working day.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-[#0D1B2A]">
                    <a data-testid="support-email" href={`mailto:${BOOKS_SUPPORT.email}`} className="inline-flex items-center gap-2 underline underline-offset-2 hover:text-[#D86B0F] transition-colors">
                      <Mail size={14} /> {BOOKS_SUPPORT.email}
                    </a>
                    <a data-testid="support-whatsapp" href={BOOKS_SUPPORT.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 underline underline-offset-2 hover:text-[#D86B0F] transition-colors">
                      WhatsApp {BOOKS_SUPPORT.whatsapp}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* CONTINUE EXPLORING */}
          <Reveal delay={0.25}>
            <div className="mt-14">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Continue exploring Chinnu</div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {CHINNU_BOOKS.filter((b) => b.slug !== slug).slice(0, 3).map((b) => (
                  <Link
                    key={b.slug}
                    to={`/books/chinnu/${b.slug}`}
                    data-testid={`order-explore-${b.slug}`}
                    className="bg-white border border-[#E7DFCF] rounded-sm p-4 flex items-center gap-3 hover:border-[#F57F17]/60 hover:shadow-[0_4px_20px_rgba(13,27,42,0.06)] transition-all duration-200"
                  >
                    <div className="w-12 shrink-0"><BookCover book={b} /></div>
                    <div className="text-[14px] text-[#0D1B2A] font-serif-d leading-tight">
                      {b.title}
                      <div className="text-[11px] text-[#5C6470] uppercase tracking-[0.16em] font-semibold mt-1 flex items-center gap-1">Explore <ArrowRight size={11} /></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
